import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { apiUrl } from '../lib/api-base';
import {
  RIFLE_TOURNAMENT_SLUG,
  formatRuleText,
  formatTieText,
  tournamentFormat,
  boardPageText,
  SHOOTER_GENDERS,
  SHOOTER_GENDER_LABELS,
  type PublicLeaderboard,
} from '../lib/shooting';
import { BIG_SCREEN_QUERY, boardView, type GenderFilter } from '../lib/scoreboard-view';
import { bigScreenPath } from '../lib/big-screen';
import { podiumTier, rowSignature, shotProgress } from '../lib/scoreboard-style';
import { PublicQueueCard } from '../components/scoreboard/PublicQueueCard';
import { PublicRound2Results } from '../components/scoreboard/PublicRound2Results';
import { subscribeScoreboard, type ScoreboardConnection } from '../lib/scoreboard-connection';

interface LiveTournament {
  slug: string;
  title: string;
  shotsPlanned: number;
  shotsCounted: number;
}

/**
 * /scoreboard/:slug shows that board. /scoreboard shows the only live tournament straight away (as the
 * rifle link always has), or a list to pick from when more than one is live.
 */
export function ScoreboardPage() {
  const params = useParams();
  const [live, setLive] = useState<LiveTournament[] | null>(null);
  useEffect(() => {
    if (params.slug) return;
    const controller = new AbortController();
    fetch(apiUrl('/api/public/shooting'), { cache: 'no-store', credentials: 'omit', signal: controller.signal })
      .then(async (res) => (res.ok ? ((await res.json()) as { tournaments?: LiveTournament[] }).tournaments ?? [] : []))
      .then(setLive)
      .catch((err: Error) => {
        if (err.name !== 'AbortError') setLive([]);
      });
    return () => controller.abort();
  }, [params.slug]);

  if (params.slug) return <ScoreboardBoard key={params.slug} slug={params.slug} />;
  if (live === null) return <ScoreboardLoading />;
  if (live.length <= 1) return <ScoreboardBoard slug={live[0]?.slug ?? RIFLE_TOURNAMENT_SLUG} />;
  return <ScoreboardList tournaments={live} />;
}

/** Real rules and empty results are shown only after the payload arrives. */
function ScoreboardLoading({ error }: { error?: string | null }) {
  return (
    <div className="scoreboard-dark min-h-screen px-4 py-6 text-slate-100 md:px-8" aria-busy="true">
      <div className="mx-auto max-w-6xl space-y-6">
        <p role="status" className="text-sm text-slate-300">Loading scores…</p>
        {error ? <p role="alert" className="text-sm text-rose-300">{error}</p> : null}
        <div aria-hidden="true" className="space-y-5 motion-safe:animate-pulse">
          <div className="h-12 w-2/3 rounded-lg bg-white/10" />
          <div className="h-5 w-1/3 rounded bg-white/10" />
          <div className="space-y-3">{Array.from({ length: 6 }, (_, index) => <div key={index} className="h-16 rounded-xl border border-white/10 bg-white/5" />)}</div>
        </div>
      </div>
    </div>
  );
}

/** Unknown /scoreboard/:slug: no live dot, rules or table, and nothing keeps retrying. */
export function ScoreboardNotFound() {
  return (
    <div className="scoreboard-dark min-h-screen px-4 py-10 text-slate-100 md:px-8">
      <div className="mx-auto max-w-xl space-y-5 text-center">
        <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-white p-1">
          <img src="/logo-ui.png?v=20261103" alt="SMYM" className="h-full w-full object-contain" />
        </span>
        <h1 className="text-2xl font-bold text-white md:text-3xl">Scoreboard not found</h1>
        <p className="text-slate-300">There is no scoreboard at this link. Check the link, or open the live scores.</p>
        <Link
          to="/scoreboard"
          className="inline-flex min-h-11 items-center rounded-[var(--radius-md)] bg-white px-5 text-sm font-semibold text-[#0a1122]"
        >
          Go to live scores
        </Link>
      </div>
    </div>
  );
}

function ScoreboardList({ tournaments }: { tournaments: LiveTournament[] }) {
  useEffect(() => {
    document.title = 'Live scores · SMYM';
  }, []);
  return (
    <div className="scoreboard-dark min-h-screen px-4 py-6 text-slate-100 md:px-8">
      <div className="mx-auto max-w-3xl space-y-5">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white p-1">
            <img src="/logo-ui.png?v=20261103" alt="SMYM" className="h-full w-full object-contain" />
          </span>
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.14em] text-slate-400">SMYM Chennai</p>
            <h1 className="text-xl font-bold text-white md:text-3xl">Live scores</h1>
          </div>
        </div>
        <ul className="space-y-3">
          {tournaments.map((item) => (
            <li key={item.slug}>
              <Link
                to={`/scoreboard/${item.slug}`}
                className="block rounded-[var(--radius-lg)] border border-white/10 bg-white/[0.04] px-4 py-4 hover:bg-white/[0.07]"
              >
                <span className="block text-lg font-semibold text-white">{item.title}</span>
                <span className="text-sm text-slate-400">
                  {formatRuleText(tournamentFormat({ shotsPerShooter: item.shotsPlanned, countedShots: item.shotsCounted }))}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function ScoreboardBoard({ slug }: { slug: string }) {
  const { hash } = useLocation();
  const [board, setBoard] = useState<PublicLeaderboard | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** The slug is unknown (404): show a not-found page and stop polling / reconnecting. */
  const [notFound, setNotFound] = useState(false);
  const [connection, setConnection] = useState<ScoreboardConnection>('connecting');
  const [query, setQuery] = useState('');
  const [bigScreen, setBigScreen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [gender, setGender] = useState<GenderFilter | 'teams'>('all');
  const [round, setRound] = useState<1 | 2>(hash === '#round2-public' ? 2 : 1);
  const showingRound2 = round === 2 && Boolean(board?.round2);
  const isFinal = board?.tournament.status === 'final';
  const queue = isFinal ? null : showingRound2 ? board?.round2Queue : board?.queue;
  const [flash, setFlash] = useState<Set<string>>(new Set());
  const [open, setOpen] = useState<string | null>(null);
  const previous = useRef<Map<string, string>>(new Map());
  const rowRefs = useRef<Map<string, HTMLLIElement>>(new Map());
  const positions = useRef<Map<string, number>>(new Map());
  const format = tournamentFormat({
    shotsPerShooter: board?.tournament.shotsPlanned,
    countedShots: board?.tournament.shotsCounted,
  });

  useEffect(() => {
    setRound(hash === '#round2-public' ? 2 : 1);
  }, [hash]);

  useEffect(() => {
    const media = window.matchMedia(BIG_SCREEN_QUERY);
    const apply = () => setBigScreen(media.matches);
    apply();
    media.addEventListener('change', apply);
    return () => media.removeEventListener('change', apply);
  }, []);

  useEffect(() => {
    document.title = notFound
      ? 'Scoreboard not found · SMYM'
      : board?.tournament.title
        ? `${board.tournament.title} · SMYM`
        : 'Live scores · SMYM';
  }, [board?.tournament.title, notFound]);

  useEffect(() => {
    if (!board) return;
    const changed = new Set<string>();
    for (const row of board.rows) {
      const signature = rowSignature(row);
      const prior = previous.current.get(row.shooterId);
      if (prior != null && prior !== signature) changed.add(row.shooterId);
      previous.current.set(row.shooterId, signature);
    }
    if (changed.size === 0) return;
    setFlash(changed);
    const timeout = window.setTimeout(() => setFlash(new Set()), 1700);
    return () => window.clearTimeout(timeout);
  }, [board]);

  useEffect(() => subscribeScoreboard(slug, {
    onBoard: setBoard, onError: setError, onNotFound: () => setNotFound(true), onConnection: setConnection,
  }), [slug]);

  const view = useMemo(
    () => boardView(board?.rows ?? [], { bigScreen, query, showAll: showAll || isFinal, displayPage: board?.displayPage ?? 0, gender: gender === 'teams' ? 'all' : gender }),
    [board, bigScreen, query, showAll, gender, isFinal],
  );
  const total = board?.rows.length ?? 0;
  /** The All / Men / Women filter only appears once someone is tagged. Teams appears with a Round 1 board. */
  const hasGender = board?.rows.some((row) => row.gender) ?? false;
  const showTeams = Boolean(board?.round1);

  // Rows glide to their new place when ranks change (skipped for reduced motion).
  useLayoutEffect(() => {
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    const seen = new Set<string>();
    for (const [id, element] of rowRefs.current) {
      seen.add(id);
      const top = element.offsetTop;
      const before = positions.current.get(id);
      positions.current.set(id, top);
      if (reduce || before == null || before === top || typeof element.animate !== 'function') continue;
      const shift = before - top;
      if (Math.abs(shift) > window.innerHeight) continue;
      element.animate([{ transform: `translateY(${shift}px)` }, { transform: 'translateY(0)' }], {
        duration: 450,
        easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)',
      });
    }
    for (const id of positions.current.keys()) if (!seen.has(id)) positions.current.delete(id);
  }, [view.rows]);

  if (notFound) return <ScoreboardNotFound />;
  if (!board) return <ScoreboardLoading error={error} />;

  const live = connection === 'open';
  const status = isFinal ? 'Final results' : live ? 'Live' : connection === 'polling' ? 'Reconnecting…' : 'Connecting…';
  const displayFormat = showingRound2 && board?.round2
    ? tournamentFormat({ shotsPerShooter: board.round2.shotsPlanned, countedShots: board.round2.shotsCounted })
    : format;
  const cols = showTeams
    ? 'grid-cols-[2.25rem_minmax(0,1fr)_3.5rem_4.25rem] gap-2 sm:grid-cols-[3rem_minmax(0,1fr)_5.5rem_5.5rem_6rem] sm:gap-3 lg:grid-cols-[3.5rem_minmax(0,1fr)_7rem_7rem_8rem] lg:gap-4 min-[100rem]:grid-cols-[5rem_minmax(0,1fr)_10rem_10rem_12rem] min-[100rem]:gap-6'
    : 'grid-cols-[2.25rem_minmax(0,1fr)_3.5rem_4.25rem] gap-2 sm:grid-cols-[3rem_minmax(0,1fr)_5.5rem_6rem] sm:gap-3 lg:grid-cols-[3.5rem_minmax(0,1fr)_7rem_8rem] lg:gap-4 min-[100rem]:grid-cols-[5rem_minmax(0,1fr)_10rem_12rem] min-[100rem]:gap-6';

  return (
    <div className="scoreboard-dark min-h-screen text-slate-100">
      <header className="sticky top-[var(--qa-banner-offset,0px)] z-20 border-b border-white/10 bg-[#0a1122]/85 px-4 py-2.5 backdrop-blur-md lg:px-8 lg:py-2 min-[100rem]:py-3">
        <div className="mx-auto flex max-w-6xl items-center gap-3 lg:gap-4 min-[100rem]:max-w-[100rem]">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white p-1 shadow-[0_0_0_3px_rgba(250,204,21,0.25)] lg:h-11 lg:w-11 min-[100rem]:h-16 min-[100rem]:w-16">
            <img src="/logo-ui.png?v=20261103" alt="SMYM" className="h-full w-full object-contain" />
          </span>
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-lg font-bold leading-tight tracking-tight text-white sm:text-2xl lg:text-[1.75rem] min-[100rem]:text-[2.4rem]">
              {board?.tournament.title ?? 'Live scores'}
            </h1>
            <p className="text-xs leading-snug text-slate-400 lg:text-sm min-[100rem]:text-base">
              SMYM Chennai · {formatRuleText(displayFormat)}
            </p>
          </div>
          <p className="flex shrink-0 items-center gap-2 text-xs font-medium text-slate-300 lg:text-sm min-[100rem]:text-base" role="status">
            {!isFinal ? <span aria-hidden className="relative flex h-2.5 w-2.5 min-[100rem]:h-3.5 min-[100rem]:w-3.5">
              {live ? <span className="absolute inset-0 rounded-full bg-emerald-400 opacity-60 motion-safe:animate-ping" /> : null}
              <span className={`relative h-full w-full rounded-full ${live ? 'bg-emerald-400' : 'bg-amber-400'}`} />
            </span> : null}
            <span className={!isFinal && live ? 'sr-only sm:not-sr-only' : ''}>{status}</span>
          </p>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-3 py-3 sm:px-4 lg:px-8 lg:py-2 min-[100rem]:max-w-[100rem] min-[100rem]:py-4">
        {board?.round2 ? (
          <div role="group" aria-label="Round" className="mb-5 grid grid-cols-2 gap-2 rounded-2xl border border-white/10 bg-white/[0.03] p-1 sm:inline-grid sm:min-w-80">
            {([1, 2] as const).map((value) => (
              <button key={value} type="button" aria-pressed={showingRound2 === (value === 2)} onClick={() => setRound(value)}
                className={`min-h-12 rounded-xl px-6 text-base font-semibold transition-colors ${showingRound2 === (value === 2) ? 'bg-[#e01b22] text-white shadow-sm' : 'text-slate-300 hover:bg-white/10'}`}>
                Round {value}
              </button>
            ))}
          </div>
        ) : null}
        {error ? <p role="alert" className="mb-3 text-sm text-rose-300">{error}</p> : null}
        {showingRound2 && board?.round2 ? (
          <div className={queue ? 'lg:grid lg:grid-cols-[minmax(0,1fr)_24rem] lg:items-start lg:gap-6' : undefined}>
            <PublicRound2Results round2={board.round2} />
            {queue ? <div className="mt-4 lg:sticky lg:top-20 lg:mt-0"><PublicQueueCard queue={queue} /></div> : null}
          </div>
        ) : (
        <section aria-label="Round 1 results">
        <h2 className="mb-3 text-xl font-bold text-white sm:text-2xl">Round 1</h2>
        {hasGender || showTeams ? (
          <div className="mb-3 grid grid-cols-2 gap-2 sm:flex sm:flex-wrap" role="group" aria-label="Show">
            {(['all', ...(hasGender ? SHOOTER_GENDERS : [])] as GenderFilter[]).map((value) => (
                  <button
                    key={value}
                    type="button"
                    aria-pressed={gender === value}
                    onClick={() => setGender(value)}
                    className={`min-h-10 rounded-full px-4 text-sm font-medium transition-colors min-[100rem]:min-h-12 min-[100rem]:px-6 min-[100rem]:text-lg ${
                      gender === value ? 'bg-white text-[#0a1122]' : 'border border-white/15 text-slate-300 hover:bg-white/10'
                    }`}
                  >
                    {value === 'all' ? 'All shooters' : SHOOTER_GENDER_LABELS[value]}
                  </button>
                ))}
            {showTeams ? (
              <button
                type="button"
                aria-pressed={gender === 'teams'}
                onClick={() => setGender('teams')}
                className={`min-h-10 rounded-full px-4 text-sm font-medium transition-colors min-[100rem]:min-h-12 min-[100rem]:px-6 min-[100rem]:text-lg ${
                  gender === 'teams' ? 'bg-white text-[#0a1122]' : 'border border-white/15 text-slate-300 hover:bg-white/10'
                }`}
              >
                Teams
              </button>
            ) : null}
          </div>
        ) : null}
        <div className="mb-3 flex flex-wrap items-center gap-2 lg:mb-0">
          {!isFinal && slug === RIFLE_TOURNAMENT_SLUG ? (
            <Link
              to={bigScreenPath(slug)}
              className="inline-flex min-h-11 shrink-0 items-center rounded-full border border-white/20 px-4 text-sm font-semibold text-white"
            >
              Big screen
            </Link>
          ) : null}
          {gender === 'teams' ? null : view.paged ? (
            <p className="flex-1 text-sm tabular-nums text-slate-400 min-[100rem]:text-base">{boardPageText(view.page, total)}</p>
          ) : (
            <label className="min-w-0 flex-1">
              <span className="sr-only">Find a shooter</span>
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Find a name or number"
                className="h-11 w-full rounded-[var(--radius-md)] border border-white/15 bg-white/5 px-3 text-base text-white placeholder:text-slate-500 focus:border-sky-400/60"
              />
            </label>
          )}
          {!isFinal && bigScreen && total > 10 && query.trim() === '' && gender === 'all' ? (
            <button
              type="button"
              onClick={() => setShowAll((value) => !value)}
              className="min-h-11 rounded-[var(--radius-md)] px-3 text-sm text-slate-400 underline-offset-2 hover:text-slate-200 hover:underline min-[100rem]:text-base"
            >
              {showAll ? 'Follow the big screen' : 'Show all'}
            </button>
          ) : null}
        </div>

        <div className={queue ? 'lg:grid lg:grid-cols-[minmax(0,1fr)_24rem] lg:items-start lg:gap-6' : undefined}>
        <div className={queue ? 'order-2 lg:order-1' : undefined}>
        {gender === 'teams' && board?.round1 ? <PublicRound1Teams round1={board.round1} /> : null}
        {gender === 'teams' ? null : (
        <>
        <div
          className={`grid ${cols} px-3 pb-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500 lg:px-4 lg:text-xs min-[100rem]:px-6 min-[100rem]:text-base`}
          aria-hidden
        >
          <span>Rank</span>
          <span>Shooter</span>
          {showTeams ? <span className="hidden sm:block">Team</span> : null}
          <span className="text-right">Shots</span>
          <span className="text-right">Total</span>
        </div>

        {view.rows.length === 0 ? (
          <p className="py-16 text-center text-slate-400">
            {board ? 'No shooter matches that search.' : 'Scores will show up here as they are entered.'}
          </p>
        ) : (
          <ol className="relative overflow-hidden rounded-[var(--radius-lg)] border border-white/10 bg-white/[0.02] shadow-[0_20px_60px_-20px_rgba(0,0,0,0.6)]">
            {view.rows.map((row, index) => {
              const expanded = open === row.shooterId;
              const planned = row.shotsPlanned ?? 0;
              const fired = row.shotsFired ?? 0;
              const ranked = row.rank > 0;
              const tier = ranked ? podiumTier(row.rank) : null;
              const progress = shotProgress(fired, planned);
              return (
                <li
                  key={row.shooterId}
                  ref={(element) => {
                    if (element) rowRefs.current.set(row.shooterId, element);
                    else rowRefs.current.delete(row.shooterId);
                  }}
                  data-podium={tier ?? undefined}
                  className={`scoreboard-row relative border-t border-white/[0.06] first:border-t-0 ${
                    index % 2 === 1 ? 'bg-white/[0.035]' : ''
                  } ${tier ? `scoreboard-podium-${tier}` : ''} ${flash.has(row.shooterId) ? 'scoreboard-flash' : ''}`}
                >
                  <button
                    type="button"
                    aria-expanded={expanded}
                    onClick={() => setOpen(expanded ? null : row.shooterId)}
                    className={`grid w-full ${cols} items-center px-3 py-2.5 text-left lg:px-4 lg:py-1.5 min-[100rem]:px-6 min-[100rem]:py-2`}
                  >
                    <span className="flex">
                      <span
                        className={`inline-flex h-7 min-w-7 items-center justify-center rounded-full px-1 text-sm font-bold tabular-nums lg:h-8 lg:min-w-8 lg:text-base min-[100rem]:h-12 min-[100rem]:min-w-12 min-[100rem]:text-2xl ${
                          tier ? `scoreboard-badge-${tier}` : ranked ? 'text-slate-300' : 'text-slate-600'
                        }`}
                      >
                        {ranked ? row.rank : '–'}
                      </span>
                    </span>
                    <span className="min-w-0">
                      <span
                        className={`block ${isFinal ? 'break-words' : 'truncate'} text-base font-semibold leading-tight lg:text-xl min-[100rem]:text-[1.95rem] ${
                          ranked ? 'text-white' : 'text-slate-300'
                        }`}
                      >
                        {row.name}
                      </span>
                      {row.bib ? (
                        <span className="block truncate text-xs leading-snug tracking-wide text-slate-500 lg:text-[13px] lg:leading-tight min-[100rem]:text-base">
                          {row.bib}
                        </span>
                      ) : null}
                      {showTeams && row.teamName ? <span className={`block ${isFinal ? 'break-words' : 'truncate'} text-xs text-slate-400 sm:hidden`}>{row.teamName}</span> : null}
                    </span>
                    {showTeams ? <span className={`hidden min-w-0 ${isFinal ? 'break-words' : 'truncate'} text-sm text-slate-300 sm:block`}>{row.teamName ?? ''}</span> : null}
                    <span className="flex flex-col items-end gap-1 min-[100rem]:gap-1.5">
                      <span className="text-sm tabular-nums text-slate-300 lg:text-base min-[100rem]:text-2xl">
                        {planned > 0 ? `${fired}/${planned}` : '–'}
                      </span>
                      {planned > 0 ? (
                        <span
                          className="block h-1 w-full max-w-[5.5rem] overflow-hidden rounded-full bg-white/10 min-[100rem]:h-2 min-[100rem]:max-w-[8.5rem]"
                          role="presentation"
                        >
                          <span
                            className={`block h-full rounded-full transition-[width] duration-500 motion-reduce:transition-none ${
                              progress >= 100 ? 'bg-emerald-400' : 'bg-sky-400'
                            }`}
                            style={{ width: `${progress}%` }}
                          />
                        </span>
                      ) : null}
                    </span>
                    <span
                      className={`text-right text-xl font-bold tabular-nums tracking-tight lg:text-[1.65rem] min-[100rem]:text-[2.7rem] ${
                        tier === 'gold' ? 'text-amber-300' : ranked ? 'text-white' : 'text-slate-600'
                      }`}
                    >
                      {ranked ? row.totalDisplay : '–'}
                    </span>
                  </button>
                  {expanded ? <ShotDetail row={row} shotCols={Math.min(format.shotsPerShooter, 15)} /> : null}
                </li>
              );
            })}
          </ol>
        )}
        </>
        )}

        {gender !== 'teams' ? <p className="mt-3 text-xs text-slate-500 lg:text-sm min-[100rem]:text-base">
          {formatTieText(format)} Tap a name for every shot.
        </p> : null}
        </div>
        {queue ? (
          <div className="order-1 mb-4 lg:sticky lg:top-20 lg:order-2 lg:mb-0">
            <PublicQueueCard queue={queue} />
          </div>
        ) : null}
        </div>
        </section>
        )}
      </main>
    </div>
  );
}

function PublicRound1Teams({ round1 }: { round1: NonNullable<PublicLeaderboard['round1']> }) {
  const tied = new Set(round1.tie?.teamIds ?? []);
  const tiedAt = round1.teams.findIndex((team) => tied.has(team.teamId));
  const cutAt = round1.tie ? (tiedAt === -1 ? round1.qualifyingTeams : tiedAt) : round1.qualifyingTeams;
  return (
    <section aria-label="Team leaderboard" className="space-y-2">
      <p className="text-sm text-slate-400">Team total. The line marks who goes to Round 2.</p>
      <ol className="overflow-hidden rounded-[var(--radius-lg)] border border-white/10">
        {round1.teams.map((team, index) => (
          <li key={team.teamId}>
            {index === cutAt && cutAt > 0 && cutAt < round1.teams.length ? (
              <p className="bg-amber-300/15 px-3 py-2 text-sm font-semibold text-amber-200">▲ Round 2 teams</p>
            ) : null}
            <div className="flex items-start justify-between gap-3 border-t border-white/10 px-3 py-2">
              <span className="min-w-0 break-words text-white">
                <span className="tabular-nums text-slate-400">{team.rank ? `${team.rank}.` : '—'} </span>
                {team.name}
              </span>
              <span className="shrink-0 text-lg font-bold tabular-nums text-amber-200">{team.totalDisplay}</span>
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}

function ShotDetail({ row, shotCols }: { row: PublicLeaderboard['rows'][number]; shotCols: number }) {
  const shots = row.shots ?? [];
  const facts = [
    `${row.tens ?? 0} tens`,
    row.latest ? `last ${row.latest.pointsDisplay} (${row.latest.label})` : null,
    shots.some((shot) => shot.counted === false) && row.droppedDisplay ? `${row.droppedDisplay} dropped` : null,
  ].filter(Boolean);
  return (
    <div className="space-y-2 border-t border-white/[0.06] bg-black/25 px-3 py-3 lg:px-4">
      <p className="text-xs text-slate-300 lg:text-sm">{facts.join(' · ')}</p>
      {shots.length === 0 ? (
        <p className="text-sm text-slate-500">{row.rank > 0 ? 'Entered as a total, without shots.' : 'No shots yet.'}</p>
      ) : (
        <ol
          className="grid grid-cols-5 gap-1.5 md:grid-cols-[repeat(var(--shot-cols),minmax(0,1fr))]"
          style={{ '--shot-cols': shotCols } as React.CSSProperties}
        >
          {shots.map((shot) => (
            <li
              key={shot.shotNumber}
              className={`rounded-[var(--radius-md)] px-1 py-1 text-center ${
                shot.counted === false ? 'border border-dashed border-white/15 text-slate-500' : 'bg-white/[0.07] text-white'
              }`}
            >
              <span className="block text-[10px] text-slate-500">
                {shot.counted === false ? `${shot.shotNumber} · out` : shot.shotNumber}
              </span>
              <span className={`text-sm font-semibold tabular-nums lg:text-base ${shot.counted === false ? 'line-through' : ''}`}>
                {shot.pointsDisplay}
              </span>
            </li>
          ))}
        </ol>
      )}
      {shots.some((shot) => shot.counted === false) ? (
        <p className="text-xs text-slate-500">Struck-through shots are the lowest and don&apos;t count.</p>
      ) : null}
    </div>
  );
}
